import { useRef, useState, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { useFokusfalle } from "./useFokusfalle.ts";
import { fehlertext } from "../../lib/fehlertext.ts";
import type { FxDeal } from "../../lib/fx.ts";
import { sicherungsNr } from "../../lib/sicherungsNummer.ts";

export interface SicherungLoeschenDialogProps {
  deal: FxDeal;
  /** Löscht die Sicherung weich; wirft bei Ablehnung (z. B. 409) mit dem Serverwortlaut im Körper (`{ error }`). */
  onLoeschen: (grund: string) => Promise<void>;
  onSchliessen: () => void;
}

/** Bestätigungsdialog "Sicherung löschen" mit optionalem Grund - gleiche Bauart wie SicherungAnlegenDialog. */
export function SicherungLoeschenDialog({ deal, onLoeschen, onSchliessen }: SicherungLoeschenDialogProps) {
  const [grund, setGrund] = useState("");
  const [fehler, setFehler] = useState("");
  const [speichert, setSpeichert] = useState(false);
  const speichertRef = useRef(false);
  const dialogRef = useRef<HTMLElement>(null);
  const grundRef = useRef<HTMLInputElement>(null);

  useFokusfalle({ dialogRef, beschaeftigtRef: speichertRef, onSchliessen, ersterFokus: () => grundRef.current });

  async function loeschen(ereignis: FormEvent<HTMLFormElement>): Promise<void> {
    ereignis.preventDefault();
    if (speichertRef.current) return;
    setFehler("");
    speichertRef.current = true;
    setSpeichert(true);
    try {
      await onLoeschen(grund.trim());
      onSchliessen();
    } catch (ursache) {
      // Inline bleibt inline (Spec R2), Text aus `fehlertext()`: der Servertext des 409 ("… noch Zuordnungen zu
      // Bestellungen …") steht hinter dem Kontext, ein Proxy-502 wird zum Satz über den Server.
      setFehler(fehlertext(ursache, "Die Sicherung konnte nicht gelöscht werden.") ?? "");
    } finally {
      speichertRef.current = false;
      setSpeichert(false);
    }
  }

  return createPortal(
    <div className="board-editor-overlay open" role="presentation" onClick={(ereignis) => {
      if (!speichertRef.current && ereignis.target === ereignis.currentTarget) onSchliessen();
    }}>
      <section ref={dialogRef} className="board-editor-dialog fx-sicherung-anlegen-dialog" role="dialog" aria-modal="true" aria-labelledby="sicherungLoeschenTitel" tabIndex={-1}>
        <form className="password-form" onSubmit={(ereignis) => void loeschen(ereignis)}>
          <div className="board-editor-head"><div className="board-editor-head-copy"><h2 id="sicherungLoeschenTitel">Sicherung löschen</h2><p>Sicherung {sicherungsNr(deal)} wird als „gelöscht (nicht übernommen)“ in der Historie geführt und kann nicht wiederhergestellt werden.</p></div></div>
          <div className="board-editor-body fx-sicherung-anlegen-fields">
            <label>Grund (optional)<input ref={grundRef} aria-label="Grund" value={grund} maxLength={500} disabled={speichert} onChange={(ereignis) => setGrund(ereignis.target.value)} /></label>
            {fehler && <p className="fx-overview-error" role="alert">{fehler}</p>}
          </div>
          <div className="board-editor-foot"><button className="btn-sec" type="button" disabled={speichert} onClick={onSchliessen}>Abbrechen</button><button className="btn-danger" type="submit" disabled={speichert}>{speichert ? "Wird gelöscht …" : "Endgültig löschen"}</button></div>
        </form>
      </section>
    </div>,
    document.body,
  );
}
